import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  grantAttendanceShare,
  fetchSharedByMe,
  revokeAttendanceShare,
} from '../../services/attendanceService';
import { fetchUsers } from '../../store/slices/userSlice';

const ShareAttendanceModal = ({ isOpen, onClose, onShared }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const reduxUsers = useSelector((state) => state.users.users || []);
  const [sharedByMe, setSharedByMe] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        dispatch(fetchUsers());
        const sharedRes = await fetchSharedByMe();
        if (cancelled) return;
        setSharedByMe(sharedRes || []);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load users');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen]);

  const currentUserId = currentUser?.id;

  const availableUsers = useMemo(() => {
    const alreadyShared = new Set(
      sharedByMe.filter((s) => s.status === 'ACTIVE').map((s) => s.viewerUserId),
    );
    return reduxUsers.filter((u) => u.id !== currentUserId && !alreadyShared.has(u.id));
  }, [reduxUsers, sharedByMe, currentUserId]);

  const toggleUser = (userId) => {
    setSelectedUserIds((prev) => (
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    ));
  };

  const handleShare = async () => {
    if (selectedUserIds.length === 0) {
      setError('Select at least one person to share with');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await grantAttendanceShare(selectedUserIds);
      setSelectedUserIds([]);
      const updated = await fetchSharedByMe();
      setSharedByMe(updated || []);
      onShared?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to share attendance');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (shareId) => {
    try {
      await revokeAttendanceShare(shareId);
      const updated = await fetchSharedByMe();
      setSharedByMe(updated || []);
      onShared?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to revoke share');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Share Attendance & Status</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Your supervisor already sees your attendance. Share here only for people outside your reporting line.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 max-h-[70vh] overflow-y-auto space-y-5">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Share with
            </p>
            {loading ? (
              <p className="text-sm text-gray-500">Loading users…</p>
            ) : availableUsers.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Everyone in your org already has access, or no other users found.
              </p>
            ) : (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
                {availableUsers.map((user) => {
                  const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username;
                  const checked = selectedUserIds.includes(user.id);
                  return (
                    <label
                      key={user.id}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleUser(user.id)}
                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-gray-900 dark:text-white">{name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {sharedByMe.filter((s) => s.status === 'ACTIVE').length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Currently shared with
              </p>
              <div className="space-y-2">
                {sharedByMe.filter((s) => s.status === 'ACTIVE').map((share) => (
                  <div
                    key={share.id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
                  >
                    <span className="text-sm text-gray-900 dark:text-white">
                      {share.viewerUserName || share.viewerUserId}
                      {share.source === 'REPORTING' ? (
                        <span className="ml-2 text-[11px] text-gray-500 dark:text-gray-400">Supervisor</span>
                      ) : null}
                    </span>
                    {share.source === 'REPORTING' ? (
                      <span className="text-xs text-gray-400">Automatic</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleRevoke(share.id)}
                        className="text-xs text-rose-600 dark:text-rose-400 hover:underline"
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Close
          </button>
          <button
            type="button"
            disabled={saving || selectedUserIds.length === 0}
            onClick={handleShare}
            className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Sharing…' : 'Share'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareAttendanceModal;
